/**
 * TASK-231: Date Transformer
 *
 * Transforms date values from various formats to standard Date or ISO string.
 * Handles: ISO 8601, Swiss (dd.mm.yyyy), US (mm/dd/yyyy), and more.
 */

/**
 * Supported date formats for parsing
 */
type DateFormat = 'iso' | 'swiss' | 'us' | 'auto';

/**
 * Regular expressions for date format detection
 */
const DATE_PATTERNS = {
  // ISO 8601: 2024-01-15, 2024-01-15T10:30:00Z
  iso: /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/,
  // Swiss/European: 15.01.2024, 15.1.2024
  swiss: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,
  // US: 01/15/2024, 1/15/2024
  us: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
  // Dash separated European: 15-01-2024
  dashEuropean: /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
  // Year first with slashes: 2024/01/15
  yearFirst: /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/,
};

/**
 * Parse a Swiss format date (dd.mm.yyyy) to Date
 */
function parseSwissDate(value: string): Date | null {
  const match = value.match(DATE_PATTERNS.swiss);
  if (!match) return null;

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // JS months are 0-indexed
  const year = parseInt(match[3], 10);

  const date = new Date(year, month, day);

  // Validate the date is real
  if (
    date.getDate() !== day ||
    date.getMonth() !== month ||
    date.getFullYear() !== year
  ) {
    return null;
  }

  return date;
}

/**
 * Parse a US format date (mm/dd/yyyy) to Date
 */
function parseUSDate(value: string): Date | null {
  const match = value.match(DATE_PATTERNS.us);
  if (!match) return null;

  const month = parseInt(match[1], 10) - 1;
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  const date = new Date(year, month, day);

  if (
    date.getDate() !== day ||
    date.getMonth() !== month ||
    date.getFullYear() !== year
  ) {
    return null;
  }

  return date;
}

/**
 * Parse a dash-separated European date (dd-mm-yyyy) to Date
 */
function parseDashEuropeanDate(value: string): Date | null {
  const match = value.match(DATE_PATTERNS.dashEuropean);
  if (!match) return null;

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const year = parseInt(match[3], 10);

  const date = new Date(year, month, day);

  if (
    date.getDate() !== day ||
    date.getMonth() !== month ||
    date.getFullYear() !== year
  ) {
    return null;
  }

  return date;
}

/**
 * Parse a year-first slash date (yyyy/mm/dd) to Date
 */
function parseYearFirstDate(value: string): Date | null {
  const match = value.match(DATE_PATTERNS.yearFirst);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);

  const date = new Date(year, month, day);

  if (
    date.getDate() !== day ||
    date.getMonth() !== month ||
    date.getFullYear() !== year
  ) {
    return null;
  }

  return date;
}

/**
 * Auto-detect and parse date from string
 */
function autoParseDate(value: string): Date | null {
  const trimmed = value.trim();

  // Try ISO first (most common from APIs)
  if (DATE_PATTERNS.iso.test(trimmed)) {
    const date = new Date(trimmed);
    return isNaN(date.getTime()) ? null : date;
  }

  // Try Swiss format
  if (DATE_PATTERNS.swiss.test(trimmed)) {
    return parseSwissDate(trimmed);
  }

  // Try US format
  if (DATE_PATTERNS.us.test(trimmed)) {
    return parseUSDate(trimmed);
  }

  // Try dash European
  if (DATE_PATTERNS.dashEuropean.test(trimmed)) {
    return parseDashEuropeanDate(trimmed);
  }

  // Try year first
  if (DATE_PATTERNS.yearFirst.test(trimmed)) {
    return parseYearFirstDate(trimmed);
  }

  // Last resort: try native Date parsing
  const date = new Date(trimmed);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Transform a date value to Date object
 *
 * @param value - Input value (string, Date, or undefined)
 * @param format - Expected format ('iso', 'swiss', 'us', 'auto')
 * @returns Date object or undefined if invalid/missing
 */
export function transformToDate(
  value: string | Date | undefined | null,
  format: DateFormat = 'auto'
): Date | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  // Already a Date
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value;
  }

  // String parsing
  if (typeof value === 'string') {
    let date: Date | null = null;

    switch (format) {
      case 'iso':
        date = DATE_PATTERNS.iso.test(value) ? new Date(value) : null;
        break;
      case 'swiss':
        date = parseSwissDate(value);
        break;
      case 'us':
        date = parseUSDate(value);
        break;
      case 'auto':
      default:
        date = autoParseDate(value);
    }

    return date && !isNaN(date.getTime()) ? date : undefined;
  }

  return undefined;
}

/**
 * Transform a date value to ISO string
 *
 * @param value - Input value (string, Date, or undefined)
 * @param format - Expected format ('iso', 'swiss', 'us', 'auto')
 * @returns ISO date string (YYYY-MM-DD) or undefined if invalid/missing
 */
export function transformToISOString(
  value: string | Date | undefined | null,
  format: DateFormat = 'auto'
): string | undefined {
  const date = transformToDate(value, format);
  if (!date) return undefined;

  // Return YYYY-MM-DD format
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Transform a date value to Swiss format string
 *
 * @param value - Input value (string, Date, or undefined)
 * @param format - Expected input format
 * @returns Swiss format string (dd.mm.yyyy) or undefined
 */
export function transformToSwissString(
  value: string | Date | undefined | null,
  format: DateFormat = 'auto'
): string | undefined {
  const date = transformToDate(value, format);
  if (!date) return undefined;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
}

/**
 * Main transform function - converts to ISO string by default
 * This is the primary export for use in mappers
 *
 * @param value - Input value
 * @returns ISO date string or undefined
 */
export function transformDate(
  value: string | Date | undefined | null
): string | undefined {
  return transformToISOString(value, 'auto');
}

/**
 * Check if a value is a valid date
 */
export function isValidDate(value: string | Date | undefined | null): boolean {
  return transformToDate(value, 'auto') !== undefined;
}
